/**
 * Missions.js
 *
 * Phase 10 — Universe-wide missions board. Aggregates available contracts
 * from every city the player has visited, plus a banner for the currently
 * active contract (and a Turn-In / Abandon shortcut if applicable).
 *
 * Backend: GET /api/missions/board → { active, cities: [{city_name,
 *   city_coords_key, city_planet_idx, contracts: [...]}, ...] }
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  Sparkles, Building2, ArrowRight, Telescope, Pickaxe, Coins, Crosshair, MapPinned, Orbit,
} from 'lucide-react';

const TYPE_META = {
  kill_hostiles: { icon: Crosshair, label: 'Combat', accent: 'text-rose-300' },
  mine_value: { icon: Pickaxe, label: 'Mining', accent: 'text-amber-300' },
  deliver_credits: { icon: Coins, label: 'Courier', accent: 'text-yellow-300' },
  scan_planets: { icon: Telescope, label: 'Survey', accent: 'text-cyan-300' },
  explore_bodies: { icon: MapPinned, label: 'Cartography', accent: 'text-emerald-300' },
};

function Missions() {
  const { api, updateCredits } = useAuth();
  const navigate = useNavigate();
  const [board, setBoard] = useState({ active: null, cities: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/missions/board');
      setBoard(data || { active: null, cities: [] });
    } catch (_e) {
      toast.error('Failed to load missions board');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const handleAccept = useCallback(async (contract, city) => {
    setBusy(true);
    try {
      await api.post('/api/city/contracts/accept', {
        contract_id: contract.id,
        coords_key: city.city_coords_key,
        planet_idx: city.city_planet_idx,
      });
      toast.success('Contract accepted');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to accept');
    } finally {
      setBusy(false);
    }
  }, [api, load]);

  const handleAbandon = useCallback(async () => {
    if (!board.active) return;
    setBusy(true);
    try {
      const c = board.active.contract;
      await api.post('/api/city/contracts/abandon', {
        coords_key: c.issuing_city_coords_key,
        planet_idx: c.issuing_city_planet_idx,
      });
      toast.success('Contract abandoned');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to abandon');
    } finally {
      setBusy(false);
    }
  }, [api, board.active, load]);

  const handleTurnIn = useCallback(async () => {
    if (!board.active) return;
    setBusy(true);
    try {
      const c = board.active.contract;
      const { data } = await api.post('/api/city/contracts/turnin', {
        coords_key: c.issuing_city_coords_key,
        planet_idx: c.issuing_city_planet_idx,
      });
      if (data?.credits != null) updateCredits(data.credits);
      toast.success(`Contract complete · +${data?.reward || 0} cr`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cannot turn in yet');
    } finally {
      setBusy(false);
    }
  }, [api, board.active, load, updateCredits]);

  const totalAvailable = useMemo(
    () => board.cities.reduce((a, c) => a + (c.contracts?.length || 0), 0),
    [board.cities],
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header breadcrumbs={[{ label: 'Missions' }]} />

      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Sparkles className="w-7 h-7 text-amber-300" />
            <div>
              <h1
                className="text-3xl font-bold text-slate-100"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                data-testid="missions-page-title"
              >
                Missions Board
              </h1>
              <p className="text-sm text-slate-400 mt-0.5">
                {loading ? 'Loading colonies…' : `${board.cities.length} colonies · ${totalAvailable} open contracts`}
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate('/explore/octants')}>
            Back to Universe
          </Button>
        </div>

        {/* Active contract banner */}
        {board.active && (
          <div
            className="rounded-xl border border-emerald-400/30 bg-emerald-900/10 p-5 mb-6"
            data-testid="missions-active-banner"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300/80">Active Contract</div>
                <h3 className="text-xl text-slate-100 font-semibold mt-1">
                  {board.active.contract?.title}
                </h3>
                <p className="text-sm text-slate-400 mt-1">{board.active.contract?.description}</p>
                <div className="mt-3">
                  <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
                    <span>Progress</span>
                    <span className="font-mono">
                      {(board.active.progress || 0).toFixed(
                        ['kill_hostiles','scan_planets','explore_bodies'].includes(board.active.contract?.type) ? 0 : 1,
                      )}
                      {' / '}
                      {Number(board.active.contract?.target || 0).toFixed(0)}
                    </span>
                  </div>
                  <Progress
                    value={Math.min(100, ((board.active.progress || 0) / (board.active.contract?.target || 1)) * 100)}
                    className="h-2 bg-slate-900/80"
                  />
                </div>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400">Reward</div>
                  <div className="text-amber-200 font-mono text-xl">+{board.active.contract?.reward} cr</div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline" size="sm"
                    onClick={() => navigate(`/play/${board.active.contract?.issuing_city_coords_key}/planet/${board.active.contract?.issuing_city_planet_idx ?? 0}`)}
                    className="border-cyan-400/40 text-cyan-200 hover:text-cyan-100"
                    data-testid="missions-active-enter-system-btn"
                  >
                    <Orbit className="w-4 h-4 mr-1.5" /> Go to Mission
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    onClick={handleAbandon} disabled={busy}
                    data-testid="missions-abandon-btn"
                  >
                    Abandon
                  </Button>
                  <Button
                    size="sm" onClick={handleTurnIn} disabled={busy}
                    className="bg-emerald-500/90 hover:bg-emerald-400 text-slate-950 font-semibold"
                    data-testid="missions-turnin-btn"
                  >
                    Turn In
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Per-city list */}
        {!loading && board.cities.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-700/60 p-10 text-center">
            <Building2 className="w-10 h-10 mx-auto text-slate-500 mb-3" />
            <p className="text-slate-300 text-lg">No colonies discovered yet.</p>
            <p className="text-slate-500 text-sm mt-2">
              Explore systems flagged as <span className="text-amber-300">Colonized</span> to find cities, then
              return here to view available contracts.
            </p>
            <Button className="mt-4" onClick={() => navigate('/explore/octants')}>Explore the Universe</Button>
          </div>
        )}

        <div className="space-y-6">
          {board.cities.map((city) => (
            <div
              key={`${city.city_coords_key}:${city.city_planet_idx}`}
              className="rounded-xl border border-slate-800/60 bg-slate-900/30 p-5"
              data-testid={`missions-city-${city.city_coords_key}-${city.city_planet_idx}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3 min-w-0">
                  <Building2 className="w-5 h-5 text-amber-300" />
                  <div className="min-w-0">
                    <div className="text-slate-100 font-semibold truncate">{city.city_name}</div>
                    <div className="text-xs text-slate-500">
                      {city.city_coords_key} · planet {city.city_planet_idx}
                    </div>
                  </div>
                </div>
                <Button
                  variant="outline" size="sm"
                  onClick={() => navigate(`/play/${city.city_coords_key}/planet/${city.city_planet_idx ?? 0}`)}
                  className="border-cyan-400/40 text-cyan-200 hover:text-cyan-100 shrink-0"
                  data-testid={`missions-enter-system-${city.city_coords_key}`}
                >
                  <Orbit className="w-4 h-4 mr-1.5" /> Enter Planet
                </Button>
              </div>
              <div className="grid gap-2">
                {(city.contracts || []).map((c) => {
                  const meta = TYPE_META[c.type] || { icon: Sparkles, label: c.type, accent: 'text-slate-300' };
                  const Icon = meta.icon;
                  const isActive = board.active && board.active.contract?.id === c.id;
                  return (
                    <div
                      key={c.id}
                      className="rounded-md border border-slate-800/60 bg-slate-950/50 px-4 py-3 flex items-center justify-between gap-3"
                      data-testid={`missions-contract-${c.id}`}
                    >
                      <div className="min-w-0 flex items-start gap-3">
                        <Icon className={`w-5 h-5 mt-0.5 ${meta.accent}`} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-slate-100 font-medium">{c.title}</span>
                            <Badge variant="outline" className={`border-slate-700 ${meta.accent}`}>{meta.label}</Badge>
                            {isActive && (
                              <Badge className="bg-emerald-500/20 text-emerald-200 border-emerald-400/40">Active</Badge>
                            )}
                          </div>
                          <div className="text-xs text-slate-400 mt-1">{c.description}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="text-right">
                          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Reward</div>
                          <div className="text-amber-200 font-mono">+{c.reward} cr</div>
                        </div>
                        <Button
                          onClick={() => handleAccept(c, city)}
                          disabled={busy || !!board.active}
                          size="sm"
                          className="bg-amber-500/90 hover:bg-amber-400 text-slate-950 font-semibold disabled:bg-slate-700 disabled:text-slate-400"
                          data-testid={`missions-accept-${c.id}`}
                        >
                          Accept <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Missions;
